// ============================================================================
// DialogEditarReserva.jsx
// Funcionalidades:
// - Editar empresa, profissional, data, horário e serviço da reserva
// - Filtros em cascata (empresa → profissional → data → horário → serviço)
// - Validação de campos obrigatórios
// - Integração com API para atualizar agendamento
// - Design responsivo (Desktop 2 colunas, Mobile 1 coluna)
// ============================================================================

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../AuthContext";
import {
  FaBuilding,
  FaUserMd,
  FaCalendarAlt,
  FaClock,
  FaList,
  FaCalendarCheck,
} from "react-icons/fa";

const DialogEditarReserva = ({
  // Controle do diálogo
  visible,
  onHide,

  // Dados da reserva
  reservaEditandoId,
  dadosCompletos,
  profissionaisFiltrados,
  servicosFiltrados,

  // Funções de filtro
  filtrarProfissionaisPorEmpresa,
  filtrarServicosPorProfissional,

  // Callbacks para o componente pai
  fetchAgendamentos,
  fetchTodosDados,
  showSuccessMessage,
  showErrorMessage,

  // Configurações
  checked,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Estados do formulário
  const [empresa, setEmpresa] = useState("");
  const [profissional, setProfissional] = useState("");
  const [data, setData] = useState("");
  const [hora, setHora] = useState("");
  const [servico, setServico] = useState("");
  const [horariosDisponiveis, setHorariosDisponiveis] = useState([]);

  // ==========================================================================
  // DETECÇÃO DE DISPOSITIVO MÓVEL
  // ==========================================================================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ==========================================================================
  // FUNÇÃO: Filtrar horários por profissional e data (versão local)
  // ==========================================================================
  const filtrarHorariosPorProfissionalEDataLocal = useCallback(
    (profissionalId, dataSelecionada) => {
      if (!dataSelecionada) {
        setHorariosDisponiveis([]);
        setHora("");
        return;
      }

      const dataObj = new Date(dataSelecionada + "T00:00:00");
      const diaSemana = dataObj.getDay();

      if (!profissionalId) {
        const horariosCadastrados = dadosCompletos.horarios
          .filter((horario) => parseInt(horario.dia_semana) === diaSemana)
          .map((horario) => horario.hora);

        const horariosUnicos = [...new Set(horariosCadastrados)].sort();
        const horariosFormatados = horariosUnicos.map((h) => ({
          label: h,
          value: h,
        }));

        setHorariosDisponiveis(horariosFormatados);
        setHora("");
        return;
      }

      const horariosCadastrados = dadosCompletos.horarios.filter(
        (horario) =>
          horario.profissional_id?.toString() === profissionalId?.toString() &&
          parseInt(horario.dia_semana) === diaSemana,
      );

      const horariosOcupados = dadosCompletos.agendamentos_existentes
        .filter(
          (agendamento) =>
            agendamento.data_atendimento === dataSelecionada &&
            agendamento.status_reserva !== "cancelada" &&
            agendamento.id !== reservaEditandoId,
        )
        .map((agendamento) => agendamento.horario);

      let horariosDisponiveisFiltrados = horariosCadastrados
        .map((horario) => horario.hora)
        .filter((horario) => !horariosOcupados.includes(horario));

      // Verificar horários já passados
      const agora = new Date();
      const hoje = new Date().toISOString().split("T")[0];
      if (dataSelecionada === hoje) {
        const horaAtual = agora.getHours();
        const minutoAtual = agora.getMinutes();
        horariosDisponiveisFiltrados = horariosDisponiveisFiltrados.filter(
          (horario) => {
            const [horaStr, minutoStr] = horario.split(":");
            const horaHorario = parseInt(horaStr);
            const minutoHorario = parseInt(minutoStr);
            if (horaHorario > horaAtual) return true;
            if (horaHorario === horaAtual && minutoHorario > minutoAtual)
              return true;
            return false;
          },
        );
      }

      const horariosOrdenados = [...new Set(horariosDisponiveisFiltrados)]
        .sort()
        .map((horario) => ({
          label: horario,
          value: horario,
        }));

      setHorariosDisponiveis(horariosOrdenados);
      setHora("");
    },
    [
      dadosCompletos.horarios,
      dadosCompletos.agendamentos_existentes,
      reservaEditandoId,
      setHorariosDisponiveis,
    ],
  );

  // ==========================================================================
  // EFFECT: Buscar dados da reserva quando o diálogo for aberto
  // ==========================================================================
  useEffect(() => {
    const fetchReservaData = async () => {
      if (visible && reservaEditandoId) {
        setLoading(true);
        try {
          const response = await fetch(
            `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendamentos/${reservaEditandoId}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${user.token}`,
                Accept: "application/json",
              },
            }
          );
          const result = await response.json();
          if (result.success && result.data) {
            const reserva = result.data;
            setEmpresa(reserva.empresa_id || "");
            setProfissional(reserva.profissional_id || "");
            setData(reserva.data_atendimento?.split("T")[0] || "");
            setHora(reserva.horario?.replace(":00", "") || "");
            setServico(reserva.servico_id || "");

            // Filtrar dependências para atualizar os dropdowns
            if (reserva.empresa_id) {
              filtrarProfissionaisPorEmpresa(reserva.empresa_id);
            }
            if (reserva.profissional_id) {
              filtrarServicosPorProfissional(reserva.profissional_id);
            }
            if (reserva.profissional_id && reserva.data_atendimento) {
              const dataAtendimento = reserva.data_atendimento?.split("T")[0];
              filtrarHorariosPorProfissionalEDataLocal(
                reserva.profissional_id,
                dataAtendimento
              );
            }
          }
        } catch (error) {
          console.error("Erro ao carregar reserva:", error);
        } finally {
          setLoading(false);
        }
      } else {
        // Resetar campos quando fechar
        setEmpresa("");
        setProfissional("");
        setData("");
        setHora("");
        setServico("");
        setHorariosDisponiveis([]);
      }
    };

    fetchReservaData();
  }, [
    visible,
    reservaEditandoId,
    user.token,
    filtrarProfissionaisPorEmpresa,
    filtrarServicosPorProfissional,
    filtrarHorariosPorProfissionalEDataLocal,
  ]);

  // ==========================================================================
  // EFFECT: Carregar horários quando profissional ou data mudarem
  // ==========================================================================
  useEffect(() => {
    if (visible && dadosCompletos.horarios?.length > 0) {
      if (profissional && data) {
        filtrarHorariosPorProfissionalEDataLocal(profissional, data);
      } else if (data) {
        filtrarHorariosPorProfissionalEDataLocal(null, data);
      }
    }
  }, [
    profissional,
    data,
    visible,
    dadosCompletos.horarios,
    filtrarHorariosPorProfissionalEDataLocal,
  ]);

  // ==========================================================================
  // FUNÇÃO: Cancelar - Limpa e fecha o diálogo
  // ==========================================================================
  const handleCancelar = () => {
    setEmpresa("");
    setProfissional("");
    setData("");
    setHora("");
    setServico("");
    setHorariosDisponiveis([]);
    onHide();
  };

  // ==========================================================================
  // HANDLER: Salvar edição (chamada à API)
  // ==========================================================================
  const handleSalvar = async () => {
    setLoading(true);
    try {
      if (!empresa || !profissional || !data || !hora || !servico) {
        throw new Error(
          "Todos os campos são obrigatórios: Empresa, Profissional, Data, Hora e Serviço"
        );
      }

      const dadosAtualizacao = {
        empresa_id: empresa,
        profissional_id: profissional,
        data_atendimento: data,
        horario: hora + ":00",
        servico_id: servico,
      };

      const response = await fetch(
        `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendamentos/${reservaEditandoId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
          body: JSON.stringify(dadosAtualizacao),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `Erro HTTP ${response.status}`);
      }

      if (result.success) {
        // Atualizar listas
        await fetchAgendamentos(checked);
        await fetchTodosDados();

        // Mostrar mensagem de sucesso
        showSuccessMessage(`Reserva atualizada com sucesso!`);

        // Fechar diálogo
        handleCancelar();
      } else {
        throw new Error(result.message || "Erro ao atualizar a reserva");
      }
    } catch (error) {
      console.error("❌ Erro ao salvar edição:", error);
      showErrorMessage(error.message || "Erro interno ao salvar as alterações");
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // HANDLER: Mudança de empresa
  // ==========================================================================
  const handleEmpresaChange = (e) => {
    const empresaId = e.value;
    setEmpresa(empresaId);
    setProfissional("");
    setData("");
    setHora("");
    setServico("");
    setHorariosDisponiveis([]);
    filtrarProfissionaisPorEmpresa(empresaId);
  };

  // ==========================================================================
  // HANDLER: Mudança de profissional
  // ==========================================================================
  const handleProfissionalChange = (e) => {
    const profissionalId = e.value;
    setProfissional(profissionalId);
    setData("");
    setHora("");
    setServico("");
    setHorariosDisponiveis([]);
    filtrarServicosPorProfissional(profissionalId);
  };

  // ==========================================================================
  // HANDLER: Mudança de data
  // ==========================================================================
  const handleDataChange = (e) => {
    if (e.value) {
      const ano = e.value.getFullYear();
      const mes = String(e.value.getMonth() + 1).padStart(2, "0");
      const dia = String(e.value.getDate()).padStart(2, "0");
      const dataFormatada = `${ano}-${mes}-${dia}`;
      setData(dataFormatada);
      setHora("");
      setServico("");

      if (profissional) {
        filtrarHorariosPorProfissionalEDataLocal(profissional, dataFormatada);
      } else {
        filtrarHorariosPorProfissionalEDataLocal(null, dataFormatada);
      }
    } else {
      setData("");
      setHora("");
      setServico("");
      setHorariosDisponiveis([]);
    }
  };

  // ==========================================================================
  // ESTILOS COMPARTILHADOS
  // ==========================================================================
  const iconStyle = {
    color: "#B83732",
    fontSize: "1rem",
    minWidth: "20px",
  };

  const labelStyle = {
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "0.85rem",
    letterSpacing: "0.3px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  // ==========================================================================
  // TEMPLATES PARA DROPDOWNS
  // ==========================================================================
  const dropdownItemTemplate = (option) => (
    <span
      style={{
        fontFamily: "'Rajdhani', sans-serif",
        fontSize: "0.9rem",
        padding: "8px 12px",
      }}
    >
      {option.label}
    </span>
  );

  const dropdownValueTemplate = (option, props) => {
    if (!option)
      return (
        <span
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            fontSize: "0.9rem",
            color: "#7f8c8d",
          }}
        >
          {props.placeholder}
        </span>
      );
    return (
      <span
        style={{
          fontFamily: "'Rajdhani', sans-serif",
          fontSize: "0.9rem",
        }}
      >
        {option.label}
      </span>
    );
  };

  // ==========================================================================
  // RENDER PRINCIPAL
  // ==========================================================================
  return (
    <Dialog
      visible={visible}
      style={{
        width: "90vw",
        maxWidth: "768px",
        fontFamily: "'Rajdhani', sans-serif",
      }}
      onHide={handleCancelar}
      draggable={false}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "-25px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ marginRight: "12px", paddingLeft: "8px" }}>
              <FaCalendarCheck
                style={{ fontSize: "2.4rem", color: "#B83732" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <div
                style={{
                  fontSize: "0.85rem",
                  width: "100%",
                  letterSpacing: "1px",
                  color: "#718096",
                }}
              >
                EDITAR
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  marginTop: "-8px",
                  color: "#2d3748",
                }}
              >
                RESERVA
              </div>
            </div>
          </div>
        </div>
      }
    >
      {/* CARD ÚNICO */}
      <div
        style={{
          backgroundColor: "#FAFAFA",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          padding: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        {/* PRIMEIRA LINHA: Empresa e Profissional */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "16px",
            marginBottom: "16px",
          }}
        >
          {/* Empresa */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={labelStyle}>
              <FaBuilding style={iconStyle} />
              <span>EMPRESA *</span>
            </div>
            <Dropdown
              value={empresa}
              onChange={handleEmpresaChange}
              options={
                dadosCompletos.empresas?.map((emp) => ({
                  label: emp.nome,
                  value: emp.id,
                })) || []
              }
              placeholder="Selecione a empresa"
              style={{
                width: "100%",
                height: "40px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
              }}
              itemTemplate={dropdownItemTemplate}
              valueTemplate={dropdownValueTemplate}
              appendTo={document.body}
            />
          </div>

          {/* Profissional */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={labelStyle}>
              <FaUserMd style={iconStyle} />
              <span>PROFISSIONAL *</span>
            </div>
            <Dropdown
              value={profissional}
              onChange={handleProfissionalChange}
              options={profissionaisFiltrados}
              placeholder="Selecione o profissional"
              disabled={!empresa}
              style={{
                width: "100%",
                height: "40px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                backgroundColor: !empresa ? "#f7fafc" : "#ffffff",
              }}
              itemTemplate={dropdownItemTemplate}
              valueTemplate={dropdownValueTemplate}
              appendTo={document.body}
            />
          </div>
        </div>

        {/* SEGUNDA LINHA: Data e Horário */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "16px",
            marginBottom: "16px",
          }}
        >
          {/* Data */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={labelStyle}>
              <FaCalendarAlt style={iconStyle} />
              <span>DATA *</span>
            </div>
            <Calendar
              value={data ? new Date(data + "T00:00:00") : null}
              onChange={handleDataChange}
              dateFormat="dd/mm/yy"
              placeholder="Selecione uma data"
              showIcon
              style={{ width: "100%" }}
              inputStyle={{
                width: "100%",
                height: "40px",
                fontFamily: "'Rajdhani', sans-serif",
                fontSize: "0.9rem",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "0 12px",
                borderTopRightRadius: "0",
                borderBottomRightRadius: "0",
              }}
              panelStyle={{
                fontFamily: "'Rajdhani', sans-serif",
                fontSize: "0.9rem",
              }}
              numberOfMonths={1}
              minDate={new Date()}
              disabled={!profissional}
            />
          </div>

          {/* Horário */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={labelStyle}>
              <FaClock style={iconStyle} />
              <span>HORÁRIO *</span>
            </div>
            <Dropdown
              value={hora}
              onChange={(e) => setHora(e.value)}
              options={horariosDisponiveis}
              placeholder="Selecione o horário"
              disabled={!data || horariosDisponiveis.length === 0}
              style={{
                width: "100%",
                height: "40px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                backgroundColor: !data ? "#f7fafc" : "#ffffff",
              }}
              itemTemplate={dropdownItemTemplate}
              valueTemplate={dropdownValueTemplate}
              appendTo={document.body}
            />
            {data && horariosDisponiveis.length === 0 && (
              <span
                style={{
                  fontSize: "0.7rem",
                  color: "#e74c3c",
                  marginTop: "4px",
                }}
              >
                Nenhum horário disponível para esta data
              </span>
            )}
          </div>
        </div>

        {/* TERCEIRA LINHA: Serviço (ocupa largura total) */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={labelStyle}>
            <FaList style={iconStyle} />
            <span>SERVIÇO *</span>
          </div>
          <Dropdown
            value={servico}
            onChange={(e) => setServico(e.value)}
            options={servicosFiltrados}
            placeholder="Selecione o serviço"
            disabled={!hora}
            style={{
              width: "100%",
              height: "40px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              backgroundColor: !hora ? "#f7fafc" : "#ffffff",
            }}
            itemTemplate={dropdownItemTemplate}
            valueTemplate={dropdownValueTemplate}
            appendTo={document.body}
          />
        </div>
      </div>

      {/* ========== BOTÕES DE AÇÃO ========== */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          flexWrap: "wrap",
          marginTop: "24px",
        }}
      >
        <Button
          label="CANCELAR"
          icon="pi pi-times"
          onClick={handleCancelar}
          className="p-button-outlined"
          style={{
            padding: "10px 28px",
            minWidth: "120px",
            borderColor: "#e2e8f0",
            color: "#718096",
            fontWeight: "600",
            borderRadius: "8px",
          }}
          disabled={loading}
        />
        <Button
          label={loading ? "SALVANDO..." : "SALVAR ALTERAÇÕES"}
          icon="pi pi-check"
          onClick={handleSalvar}
          style={{
            padding: "10px 28px",
            minWidth: "180px",
            fontWeight: "600",
            borderRadius: "8px",
            backgroundColor: "#B83732",
            borderColor: "#B83732",
            color: "white",
          }}
          disabled={!empresa || !profissional || !data || !hora || !servico || loading}
          loading={loading}
        />
      </div>

      {/* ========== CSS PERSONALIZADO ========== */}
      <style>
        {`
        /* Ajuste do ícone do calendário para ficar do mesmo tamanho do input */
        .p-calendar .p-button.p-button-icon-only {
          width: 40px !important;
          height: 40px !important;
          background-color: #f7fafc !important;
          border: 1px solid #e2e8f0 !important;
          border-left: none !important;
          border-top-right-radius: 8px !important;
          border-bottom-right-radius: 8px !important;
          color: #B83732 !important;
        }
        .p-calendar .p-button.p-button-icon-only:hover {
          background-color: #edf2f7 !important;
        }
        .p-calendar .p-button-icon {
          font-size: 0.9rem !important;
        }
      `}
      </style>
    </Dialog>
  );
};

export default DialogEditarReserva;